import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  PlayIcon,
  StopIcon,
  PauseIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChatBubbleLeftEllipsisIcon,
  EyeIcon,
  UserGroupIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { api } from '../services/api';
import toast from 'react-hot-toast';

const AutoComments = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { socket } = useSocket();

  const [configs, setConfigs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [sourceAccounts, setSourceAccounts] = useState([]);
  const [viewCheckerAccounts, setViewCheckerAccounts] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [expandedConfigs, setExpandedConfigs] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [historyMap, setHistoryMap] = useState({});
  const [statsMap, setStatsMap] = useState({});
  const [historyFilters, setHistoryFilters] = useState({});
  const [serviceUrls, setServiceUrls] = useState({});

  const [formData, setFormData] = useState({
    name: '',
    accountIds: [],
    modelId: '',
    sourceAccountIds: [],
    viewCheckerAccountIds: [],
    maxTweetAgeMinutes: 30,
    maxCommentsPerHourPerAccount: 5,
    maxCommentsPerDayPerAccount: 30,
    delayBetweenCommentsMin: 30,
    delayBetweenCommentsMax: 120,
    minAuthorFollowers: 1000,
    maxAuthorFollowers: 0
  });

  const [newTextInput, setNewTextInput] = useState('');
  const [uploadingImages, setUploadingImages] = useState({});

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/dashboard');
      toast.error('Admin access required');
      return;
    }
    loadConfigs();
    loadAccounts();
    loadSourceAccounts();
    loadViewCheckerAccounts();
    loadModels();
    loadServiceUrls();
  }, [navigate, user]);

  useEffect(() => {
    if (showCreateModal) {
      loadAccounts();
      loadSourceAccounts();
      loadViewCheckerAccounts();
    }
  }, [showCreateModal]);

  useEffect(() => {
    if (!socket) return;
    socket.emit('join-room', 'auto-comment-page');

    socket.on('auto-comment-progress', (data) => {
      setConfigs(prev => prev.map(c =>
        c._id === data.configId
          ? { ...c, progress: { ...c.progress, ...data.progress }, accountStatus: data.accountStatus || c.accountStatus }
          : c
      ));
    });

    socket.on('auto-comment-status', (data) => {
      setConfigs(prev => prev.map(c =>
        c._id === data.configId ? { ...c, status: data.status } : c
      ));
    });

    return () => {
      socket.emit('leave-room', 'auto-comment-page');
      socket.off('auto-comment-progress');
      socket.off('auto-comment-status');
    };
  }, [socket]);

  const loadConfigs = async () => {
    try {
      const res = await api.get('/auto-comment/configs');
      setConfigs(res.data || []);
    } catch (e) {
      console.error('Error loading configs:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const res = await api.get('/auto-comment/accounts');
      setAccounts(res.data.accounts || []);
    } catch (e) { console.error(e); }
  };

  const loadSourceAccounts = async () => {
    try {
      const res = await api.get('/auto-comment/source-accounts');
      setSourceAccounts(res.data.accounts || []);
    } catch (e) { console.error(e); }
  };

  const loadViewCheckerAccounts = async () => {
    try {
      const res = await api.get('/auto-comment/view-checker-accounts');
      setViewCheckerAccounts(res.data.accounts || []);
    } catch (e) { console.error(e); }
  };

  const loadModels = async () => {
    try {
      const res = await api.get('/auto-comment/models');
      setModels(res.data.models || []);
    } catch (e) { console.error(e); }
  };

  const loadServiceUrls = async () => {
    try {
      const res = await api.get('/config/services');
      setServiceUrls(res.data || {});
    } catch (e) { console.error(e); }
  };

  const loadHistory = async (configId, filters = {}) => {
    try {
      const params = new URLSearchParams({ limit: '50', page: filters.page || '1' });
      if (filters.accountId) params.set('accountId', filters.accountId);
      if (filters.status) params.set('status', filters.status);
      if (filters.date) params.set('date', filters.date);
      const res = await api.get(`/auto-comment/configs/${configId}/history?${params}`);
      setHistoryMap(prev => ({ ...prev, [configId]: res.data }));
    } catch (e) { console.error(e); }
  };

  const loadStats = async (configId, days = 7) => {
    try {
      const filter = historyFilters[configId] || {};
      const params = new URLSearchParams({ days: String(days) });
      if (filter.accountId) params.set('accountId', filter.accountId);
      const res = await api.get(`/auto-comment/configs/${configId}/stats?${params}`);
      setStatsMap(prev => ({ ...prev, [configId]: res.data }));
    } catch (e) { console.error(e); }
  };

  const toggleExpand = (configId) => {
    setExpandedConfigs(prev => {
      const next = new Set(prev);
      if (next.has(configId)) {
        next.delete(configId);
      } else {
        next.add(configId);
        loadHistory(configId);
        loadStats(configId);
      }
      return next;
    });
  };

  /* ── group selection handlers ── */
  const handleToggleGroup = (groupName) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      return next;
    });
  };

  const handleSelectGroup = (groupName) => {
    const groupAccounts = accounts
      .filter(acc => acc.group && String(acc.group).trim() === groupName)
      .filter(acc => acc.hasValidSession === true && acc.inUse !== true);
    const groupIds = groupAccounts.map(acc => acc._id);
    const allSelected = groupIds.every(id => formData.accountIds.includes(id));
    if (allSelected) {
      setFormData({ ...formData, accountIds: formData.accountIds.filter(id => !groupIds.includes(id)) });
    } else {
      setFormData({ ...formData, accountIds: [...new Set([...formData.accountIds, ...groupIds])] });
    }
  };

  const handleSelectAllAccounts = () => {
    const selectable = accounts.filter(acc => acc.hasValidSession === true && acc.inUse !== true);
    const allSelected = selectable.every(acc => formData.accountIds.includes(acc._id));
    if (allSelected) {
      setFormData({ ...formData, accountIds: [] });
    } else {
      setFormData({ ...formData, accountIds: selectable.map(acc => acc._id) });
    }
  };

  /* ── form handlers ── */
  const resetForm = () => {
    setFormData({
      name: '', accountIds: [], modelId: '', sourceAccountIds: [], viewCheckerAccountIds: [],
      maxTweetAgeMinutes: 30, maxCommentsPerHourPerAccount: 5,
      maxCommentsPerDayPerAccount: 30, delayBetweenCommentsMin: 30,
      delayBetweenCommentsMax: 120, minAuthorFollowers: 1000, maxAuthorFollowers: 0
    });
    setEditingConfig(null);
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) return toast.error('Name is required');
    if (!formData.accountIds.length) return toast.error('Select at least one account');

    try {
      if (editingConfig) {
        const payload = {
          ...formData,
          viewCheckerAccountIds: Array.isArray(formData.viewCheckerAccountIds) ? formData.viewCheckerAccountIds : []
        };
        await api.put(`/auto-comment/configs/${editingConfig._id}`, payload);
        toast.success('Config updated');
      } else {
        const payload = {
          ...formData,
          viewCheckerAccountIds: Array.isArray(formData.viewCheckerAccountIds) ? formData.viewCheckerAccountIds : []
        };
        await api.post('/auto-comment/configs', payload);
        toast.success('Config created');
      }
      setShowCreateModal(false);
      resetForm();
      loadConfigs();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this auto-comment config and all its history?')) return;
    try {
      await api.delete(`/auto-comment/configs/${id}`);
      toast.success('Deleted');
      loadConfigs();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    }
  };

  const getAutoCommentServiceUrl = () => serviceUrls.autocomment;

  const handleStart = async (id) => {
    try {
      const svcUrl = getAutoCommentServiceUrl();
      if (!svcUrl) { toast.error('Auto-comment service URL not configured'); return; }
      await axios.post(`${svcUrl}/api/auto-comment/configs/${id}/start`, {}, {
        timeout: 60000,
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Started');
      loadConfigs();
    } catch (e) {
      if (e.code === 'ECONNABORTED' || e.message?.includes('timeout')) {
        toast.error('Request timed out. Auto-comment service may be initializing.');
      } else {
        toast.error(e.response?.data?.error || 'Failed to start');
      }
    }
  };

  const handlePause = async (id) => {
    try {
      const svcUrl = getAutoCommentServiceUrl();
      if (!svcUrl) { toast.error('Auto-comment service URL not configured'); return; }
      await axios.post(`${svcUrl}/api/auto-comment/configs/${id}/pause`, {}, {
        timeout: 60000,
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Paused');
      loadConfigs();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    }
  };

  const handleStop = async (id) => {
    try {
      const svcUrl = getAutoCommentServiceUrl();
      if (!svcUrl) { toast.error('Auto-comment service URL not configured'); return; }
      await axios.post(`${svcUrl}/api/auto-comment/configs/${id}/stop`, {}, {
        timeout: 60000,
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Stopped');
      loadConfigs();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      accountIds: (config.accountIds || []).map(a => a._id || a),
      modelId: config.modelId?._id || config.modelId || '',
      sourceAccountIds: (config.sourceAccountIds || []).map(a => a._id || a),
      viewCheckerAccountIds: (config.viewCheckerAccountIds || []).map(a => a._id || a),
      maxTweetAgeMinutes: config.maxTweetAgeMinutes || 30,
      maxCommentsPerHourPerAccount: config.maxCommentsPerHourPerAccount || 5,
      maxCommentsPerDayPerAccount: config.maxCommentsPerDayPerAccount || 30,
      delayBetweenCommentsMin: config.delayBetweenCommentsMin || 30,
      delayBetweenCommentsMax: config.delayBetweenCommentsMax || 120,
      minAuthorFollowers: config.minAuthorFollowers || 1000,
      maxAuthorFollowers: config.maxAuthorFollowers || 0
    });
    setShowCreateModal(true);
  };

  /* ── content management ── */
  const handleAddTexts = async (configId) => {
    if (!newTextInput.trim()) return;
    const texts = newTextInput.split('\n').map(t => t.trim()).filter(t => t);
    if (texts.length === 0) return;
    try {
      await api.post(`/auto-comment/configs/${configId}/texts`, { texts });
      setNewTextInput('');
      loadConfigs();
      toast.success(`Added ${texts.length} text(s)`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to add texts');
    }
  };

  const handleDeleteText = async (configId, textId) => {
    try {
      await api.delete(`/auto-comment/configs/${configId}/texts/${textId}`);
      loadConfigs();
    } catch (e) {
      toast.error('Failed to delete text');
    }
  };

  const handleUploadImages = async (configId, files) => {
    if (!files || files.length === 0) return;
    setUploadingImages(prev => ({ ...prev, [configId]: true }));
    try {
      const formDataObj = new FormData();
      for (const f of files) formDataObj.append('images', f);
      const automationUrl = serviceUrls.automation;
      if (automationUrl) {
        await axios.post(`${automationUrl}/api/auto-comment/configs/${configId}/images`, formDataObj, {
          timeout: 60000,
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      } else {
        await api.post(`/auto-comment/configs/${configId}/images`, formDataObj, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      loadConfigs();
      toast.success(`Uploaded ${files.length} image(s)`);
    } catch (e) {
      if (!serviceUrls.automation) {
        toast.error('Automation service not available. Cannot upload images.');
      } else {
        toast.error(e.response?.data?.error || 'Failed to upload images');
      }
    } finally {
      setUploadingImages(prev => ({ ...prev, [configId]: false }));
    }
  };

  const handleDeleteImage = async (configId, imageId) => {
    try {
      const automationUrl = serviceUrls.automation;
      if (automationUrl) {
        await axios.delete(`${automationUrl}/api/auto-comment/configs/${configId}/images/${imageId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      } else {
        await api.delete(`/auto-comment/configs/${configId}/images/${imageId}`);
      }
      loadConfigs();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to delete image');
    }
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const path = url.startsWith('/') ? url : '/' + url;
    const isAutoCommentUpload = path.includes('/auto-comments/');
    const base = isAutoCommentUpload && serviceUrls.automation
      ? serviceUrls.automation
      : (serviceUrls.main || (typeof window !== 'undefined' ? window.location.origin : ''));
    return base ? `${base.replace(/\/$/, '')}${path}` : path;
  };

  const getServeImageUrl = (configId, imageId) => {
    if (!configId || !imageId) return null;
    const base = serviceUrls.main || (typeof window !== 'undefined' ? window.location.origin : '');
    if (!base) return null;
    return `${base.replace(/\/$/, '')}/api/auto-comment/serve-image/${configId}/${imageId}`;
  };

  const statusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'paused': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString() : '-';

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`relative overflow-hidden rounded-2xl ${
        isDarkMode 
          ? 'bg-gradient-to-tl from-slate-900 via-amber-950 to-slate-900' 
          : 'bg-gradient-to-tl from-amber-50 via-yellow-100 to-amber-100'
      }`}>
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className={`absolute -bottom-16 -left-16 w-72 h-72 rounded-full blur-3xl ${
            isDarkMode ? 'bg-amber-600/20' : 'bg-amber-300/40'
          }`} />
          <div className={`absolute -top-20 right-1/3 w-80 h-80 rounded-full blur-3xl ${
            isDarkMode ? 'bg-yellow-600/15' : 'bg-yellow-300/30'
          }`} />
          <div className={`absolute bottom-1/3 -right-10 w-56 h-56 rounded-full blur-3xl ${
            isDarkMode ? 'bg-amber-600/10' : 'bg-amber-200/30'
          }`} />
          
          <div 
            className={`absolute inset-0 ${isDarkMode ? 'opacity-[0.03]' : 'opacity-[0.08]'}`}
            style={{
              backgroundImage: `linear-gradient(${isDarkMode ? '#fff' : '#d97706'} 1px, transparent 1px), linear-gradient(90deg, ${isDarkMode ? '#fff' : '#d97706'} 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          />
        </div>
        
        <div className="relative px-8 py-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className={`relative flex items-center justify-center w-16 h-16 rounded-2xl ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-amber-500 to-yellow-600 shadow-lg shadow-amber-500/30' 
                  : 'bg-gradient-to-br from-amber-500 to-yellow-500 shadow-lg shadow-amber-400/40'
              }`}>
                <ChatBubbleLeftEllipsisIcon className="w-8 h-8 text-white" />
                <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                  isDarkMode ? 'bg-yellow-400' : 'bg-yellow-500'
                }`}>
                  <SparklesIcon className="w-3 h-3 text-white" />
                </div>
              </div>
              
              <div>
                <h1 className={`text-3xl font-bold tracking-tight ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  Auto Comments
                </h1>
                <p className={`mt-1.5 text-base ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  Automatically comment on fresh tweets from the For You feed
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => { resetForm(); setShowCreateModal(true); }}
                className={`inline-flex items-center px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-white shadow-lg shadow-amber-500/30 hover:shadow-amber-500/40' 
                    : 'bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-500 hover:to-yellow-500 text-white shadow-lg shadow-amber-500/30 hover:shadow-amber-500/40'
                }`}
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Config
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      {configs.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Comments', value: configs.reduce((s, c) => s + (c.progress?.totalComments || 0), 0), icon: ChatBubbleLeftEllipsisIcon, color: 'blue' },
            { label: 'Total Views', value: configs.reduce((s, c) => s + (c.progress?.totalViews || 0), 0), icon: EyeIcon, color: 'green' },
            { label: 'Active Configs', value: configs.filter(c => c.status === 'active').length, icon: PlayIcon, color: 'emerald' },
            { label: 'Total Accounts', value: new Set(configs.flatMap(c => (c.accountIds || []).map(a => a._id || a))).size, icon: UserGroupIcon, color: 'purple' }
          ].map(card => (
            <div key={card.label} className={`rounded-xl p-4 ${isDarkMode ? 'bg-gray-800/50 border border-gray-700/50' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-${card.color}-100 dark:bg-${card.color}-900/30`}>
                  <card.icon className={`h-5 w-5 text-${card.color}-600 dark:text-${card.color}-400`} />
                </div>
                <div>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{card.label}</p>
                  <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{card.value.toLocaleString()}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Config list */}
      {configs.length === 0 ? (
        <div className={`text-center py-12 rounded-xl ${isDarkMode ? 'bg-gray-800/50 border border-gray-700/50' : 'bg-white border border-gray-200'}`}>
          <ChatBubbleLeftEllipsisIcon className={`mx-auto h-12 w-12 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
          <h3 className={`mt-2 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>No auto-comment configs</h3>
          <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Create one to start commenting automatically.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {configs.map(config => {
            const isExpanded = expandedConfigs.has(config._id);
            const stats = statsMap[config._id];
            const historyData = historyMap[config._id];

            return (
              <div key={config._id} className={`rounded-xl overflow-hidden ${isDarkMode ? 'bg-gray-800/50 border border-gray-700/50' : 'bg-white border border-gray-200'}`}>
                {/* Config header */}
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <button onClick={() => toggleExpand(config._id)} className="shrink-0">
                        {isExpanded
                          ? <ChevronUpIcon className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                          : <ChevronDownIcon className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        }
                      </button>
                      <div className="min-w-0">
                        <h3 className={`text-sm font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{config.name}</h3>
                        <div className="flex items-center gap-2 mt-1 text-xs">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(config.status)}`}>
                            {config.status}
                          </span>
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                            {(config.accountIds || []).length} account(s) &middot; {config.progress?.totalComments || 0} comments
                          </span>
                          <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>
                            &middot; {(config.commentTexts || []).length} texts, {(config.commentImages || []).length} images
                            {(config.viewCheckerAccountIds || []).length > 0 ? ` · ${(config.viewCheckerAccountIds || []).length} checker(s)` : ''}
                            {config.modelId?.name ? ` (fallback: ${config.modelId.name})` : ''}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {config.status === 'stopped' || config.status === 'paused' ? (
                        <button onClick={() => handleStart(config._id)} className="p-1.5 rounded-lg bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/50" title="Start">
                          <PlayIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                        </button>
                      ) : null}
                      {config.status === 'active' ? (
                        <>
                          <button onClick={() => handlePause(config._id)} className="p-1.5 rounded-lg bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/50" title="Pause">
                            <PauseIcon className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                          </button>
                          <button onClick={() => handleStop(config._id)} className="p-1.5 rounded-lg bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50" title="Stop">
                            <StopIcon className="h-4 w-4 text-red-600 dark:text-red-400" />
                          </button>
                        </>
                      ) : null}
                      <button onClick={() => handleEdit(config)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700" title="Edit">
                        <PencilIcon className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                      </button>
                      <button onClick={() => handleDelete(config._id)} className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30" title="Delete">
                        <TrashIcon className="h-4 w-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className={`border-t ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200'}`}>
                    {/* Per-account status */}
                    <div className="p-4">
                      <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Per Account</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {(config.accountStatus || []).map(acc => {
                          const accountObj = (config.accountIds || []).find(a => (a._id || a).toString() === acc.accountId?.toString());
                          const username = accountObj?.username || acc.accountId?.toString()?.slice(0, 8);
                          return (
                            <div key={acc.accountId} className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>@{username}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${statusColor(acc.status)}`}>{acc.status}</span>
                              </div>
                              <div className={`text-xs space-y-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                <div>Comments: {acc.commentsSent || 0} ({acc.commentsToday || 0} today)</div>
                                <div>Failed: {acc.commentsFailed || 0}</div>
                                {acc.lastCommentAt && <div>Last: {formatDate(acc.lastCommentAt)}</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Content Management (Texts + Images) */}
                    <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200'}`}>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Comment Content ({(config.commentTexts || []).length} texts, {(config.commentImages || []).length} images)
                      </h4>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Texts */}
                        <div>
                          <div className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Texts</div>
                          <div className={`rounded-lg border p-2 mb-2 max-h-40 overflow-y-auto space-y-1 ${isDarkMode ? 'border-gray-600 bg-gray-700/30' : 'border-gray-200 bg-gray-50'}`}>
                            {(config.commentTexts || []).length === 0 ? (
                              <p className={`text-xs py-2 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No texts added yet</p>
                            ) : (
                              (config.commentTexts || []).map(t => (
                                <div key={t._id} className={`flex items-center justify-between gap-2 px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700/50' : 'bg-white'}`}>
                                  <span className={`text-xs truncate flex-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t.text}</span>
                                  <button onClick={() => handleDeleteText(config._id, t._id)} className="shrink-0 text-red-400 hover:text-red-600">
                                    <XMarkIcon className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ))
                            )}
                          </div>
                          <div className="flex gap-2">
                            <textarea
                              value={newTextInput}
                              onChange={e => setNewTextInput(e.target.value)}
                              placeholder="Enter comment texts (one per line)..."
                              rows={2}
                              className={`flex-1 rounded-lg border px-2 py-1.5 text-xs ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'}`}
                            />
                            <button
                              onClick={() => handleAddTexts(config._id)}
                              disabled={!newTextInput.trim()}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed self-end"
                            >
                              Add
                            </button>
                          </div>
                        </div>

                        {/* Images */}
                        <div>
                          <div className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Images</div>
                          <div className={`rounded-lg border p-2 mb-2 min-h-[60px] max-h-40 overflow-y-auto ${isDarkMode ? 'border-gray-600 bg-gray-700/30' : 'border-gray-200 bg-gray-50'}`}>
                            {(config.commentImages || []).length === 0 ? (
                              <p className={`text-xs py-2 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No images uploaded yet</p>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                {(config.commentImages || []).map(img => (
                                  <div key={img._id} className="relative group w-14 h-14">
                                    <img
                                      src={getImageUrl(img.url) || getServeImageUrl(config._id, img._id)}
                                      alt=""
                                      className="w-14 h-14 rounded-lg object-cover border border-gray-200 dark:border-gray-600"
                                      onError={e => { e.target.src = ''; e.target.className = 'w-14 h-14 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'; }}
                                    />
                                    <button
                                      onClick={() => handleDeleteImage(config._id, img._id)}
                                      className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <XMarkIcon className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <label className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-colors ${
                            uploadingImages[config._id]
                              ? 'bg-gray-300 dark:bg-gray-600 cursor-wait'
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}>
                            {uploadingImages[config._id] ? 'Uploading...' : 'Upload Images'}
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              className="hidden"
                              disabled={uploadingImages[config._id]}
                              onChange={e => handleUploadImages(config._id, Array.from(e.target.files))}
                            />
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Stats summary */}
                    {stats && (
                      <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Stats (Last 7 Days)</h4>
                          <div className="flex items-center gap-3 text-xs">
                            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                              {stats.totalComments} comments &middot; {stats.totalViews.toLocaleString()} views &middot; {stats.totalFailed} failed
                            </span>
                          </div>
                        </div>
                        {/* Daily breakdown */}
                        {stats.perDay?.length > 0 && (
                          <div className="grid grid-cols-7 gap-1 mb-4">
                            {stats.perDay.map(day => (
                              <div key={day._id} className={`text-center p-2 rounded ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                                <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{day._id.slice(5)}</div>
                                <div className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{day.count}</div>
                                <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{day.views} views</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Per-account stats */}
                        {stats.perAccount?.length > 0 && (
                          <div className="space-y-1">
                            {stats.perAccount.map(acc => (
                              <div key={acc._id} className={`flex items-center justify-between text-xs p-2 rounded ${isDarkMode ? 'bg-gray-700/20' : 'bg-gray-50'}`}>
                                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>@{acc.username}</span>
                                <div className="flex items-center gap-4">
                                  <span>{acc.count} comments</span>
                                  <span>{acc.views} views</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Comment history with image previews */}
                    {historyData && (
                      <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Comment History ({historyData.total})
                          </h4>
                          <div className="flex items-center gap-2">
                            <select
                              value={historyFilters[config._id]?.status || ''}
                              onChange={e => {
                                const f = { ...historyFilters, [config._id]: { ...historyFilters[config._id], status: e.target.value } };
                                setHistoryFilters(f);
                                loadHistory(config._id, f[config._id]);
                              }}
                              className={`text-xs rounded-md border px-2 py-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                            >
                              <option value="">All Status</option>
                              <option value="success">Success</option>
                              <option value="failed">Failed</option>
                            </select>
                            <input
                              type="date"
                              value={historyFilters[config._id]?.date || ''}
                              onChange={e => {
                                const f = { ...historyFilters, [config._id]: { ...historyFilters[config._id], date: e.target.value } };
                                setHistoryFilters(f);
                                loadHistory(config._id, f[config._id]);
                              }}
                              className={`text-xs rounded-md border px-2 py-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                            />
                          </div>
                        </div>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {(historyData.history || []).map(h => (
                            <div key={h._id} className={`flex items-start gap-3 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                              {/* Image thumbnail */}
                              {h.imageUrl && (
                                <div className="shrink-0">
                                  <img
                                    src={getImageUrl(h.imageUrl)}
                                    alt="Comment"
                                    className="w-12 h-12 rounded-lg object-cover border border-gray-200 dark:border-gray-600"
                                    onError={e => { e.target.src = ''; e.target.className = 'w-12 h-12 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'; }}
                                  />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                    @{h.accountId?.username || '?'}
                                  </span>
                                  <span className={`text-xs ${h.status === 'success' ? 'text-green-500' : 'text-red-500'}`}>
                                    {h.status}
                                  </span>
                                  {h.viewsCount != null && (
                                    <span className={`text-xs flex items-center gap-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      <EyeIcon className="h-3 w-3" /> {h.viewsCount}
                                    </span>
                                  )}
                                  <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {formatDate(h.postedAt)}
                                  </span>
                                </div>
                                <p className={`text-xs truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {h.commentText}
                                </p>
                                <p className={`text-xs truncate mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  Replied to @{h.parentAuthorUsername}: {h.parentTweetText?.slice(0, 80)}
                                </p>
                                {h.tweetId && (
                                  <a
                                    href={`https://x.com/i/status/${h.tweetId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-500 hover:underline mt-1 inline-block"
                                  >
                                    View on X
                                  </a>
                                )}
                              </div>
                            </div>
                          ))}
                          {(historyData.history || []).length === 0 && (
                            <p className={`text-sm text-center py-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No comments yet</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => { setShowCreateModal(false); resetForm(); }} />
            <div className={`relative w-full max-w-2xl rounded-2xl shadow-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 max-h-[90vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {editingConfig ? 'Edit Config' : 'New Auto-Comment Config'}
                </h2>
                <button onClick={() => { setShowCreateModal(false); resetForm(); }} className={`p-1 rounded-lg ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Name</label>
                  <input
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full rounded-lg border px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    placeholder="My auto-comment config"
                  />
                </div>

                {/* Model selection (optional fallback) */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Fallback Model <span className={`text-xs font-normal ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>(optional — used if no texts/images added to config)</span>
                  </label>
                  <select
                    value={formData.modelId}
                    onChange={e => setFormData({ ...formData, modelId: e.target.value })}
                    className={`w-full rounded-lg border px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  >
                    <option value="">None (use config's own content)</option>
                    {models.map(m => (
                      <option key={m._id} value={m._id}>
                        {m.name} ({m.postingConfig?.texts?.length || 0} texts, {m.postingConfig?.images?.length || 0} images)
                      </option>
                    ))}
                  </select>
                </div>

                {/* Comment accounts */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Commenting Accounts ({formData.accountIds.length} selected)
                  </label>
                  <div className={`max-h-52 overflow-y-auto rounded-lg border p-2 space-y-1 ${isDarkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-300 bg-gray-50'}`}>
                    {accounts.length === 0 ? (
                      <p className="text-xs text-gray-400">No active accounts found.</p>
                    ) : (
                      <>
                        {/* Select All */}
                        <div className="flex items-center gap-2 pb-2 mb-2 border-b border-gray-200 dark:border-gray-700">
                          <input
                            type="checkbox"
                            checked={accounts.filter(a => a.hasValidSession && !a.inUse).length > 0 &&
                              accounts.filter(a => a.hasValidSession && !a.inUse).every(a => formData.accountIds.includes(a._id))}
                            onChange={handleSelectAllAccounts}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Select All</span>
                        </div>

                        {/* Groups */}
                        {(() => {
                          const accountGroups = [...new Set(accounts
                            .map(a => a.group)
                            .filter(g => g != null && String(g).trim() !== '')
                            .map(g => String(g).trim()))];
                          if (accountGroups.length === 0) return null;
                          return (
                            <div className="space-y-1 mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                              <div className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Groups</div>
                              {accountGroups.sort().map(groupName => {
                                const groupAccs = accounts.filter(a => a.group && String(a.group).trim() === groupName);
                                const selectable = groupAccs.filter(a => a.hasValidSession && !a.inUse);
                                const selectableIds = selectable.map(a => a._id);
                                const allGroupSelected = selectable.length > 0 && selectableIds.every(id => formData.accountIds.includes(id));
                                const isExpanded = expandedGroups.has(groupName);
                                return (
                                  <div key={groupName} className="space-y-1">
                                    <div className="flex items-center gap-2">
                                      <button type="button" onClick={() => handleToggleGroup(groupName)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                        <ChevronDownIcon className={`h-4 w-4 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                                      </button>
                                      <input
                                        type="checkbox"
                                        checked={allGroupSelected}
                                        onChange={() => handleSelectGroup(groupName)}
                                        disabled={selectable.length === 0}
                                        className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${selectable.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      />
                                      <span className={`text-sm font-medium ${selectable.length === 0 ? 'text-gray-400 dark:text-gray-500' : isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {groupName} ({selectable.length}/{groupAccs.length})
                                      </span>
                                    </div>
                                    {isExpanded && (
                                      <div className="ml-6 space-y-1 mt-1">
                                        {groupAccs.map(account => {
                                          const valid = account.hasValidSession === true;
                                          const inUse = account.inUse === true;
                                          const disabled = !valid || inUse;
                                          return (
                                            <label key={account._id} className="flex items-center justify-between">
                                              <div className="flex items-center flex-1">
                                                <input
                                                  type="checkbox"
                                                  checked={formData.accountIds.includes(account._id)}
                                                  onChange={e => {
                                                    const ids = e.target.checked
                                                      ? [...formData.accountIds, account._id]
                                                      : formData.accountIds.filter(id => id !== account._id);
                                                    setFormData({ ...formData, accountIds: ids });
                                                  }}
                                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                  disabled={disabled}
                                                />
                                                <span className={`ml-2 text-sm ${disabled ? 'text-gray-400 dark:text-gray-500' : isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                                  {account.displayName || account.username}
                                                </span>
                                              </div>
                                              {!valid && <span className="ml-2 text-xs text-red-600 dark:text-red-400">No Session</span>}
                                              {valid && inUse && <span className="ml-2 text-xs text-orange-600 dark:text-orange-400">In Use</span>}
                                              {valid && !inUse && <span className="ml-2 text-xs text-green-600 dark:text-green-400">Ready</span>}
                                            </label>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}

                        {/* Ungrouped accounts */}
                        {(() => {
                          const ungrouped = accounts.filter(a => !a.group || String(a.group).trim() === '');
                          const hasGroups = accounts.some(a => a.group && String(a.group).trim() !== '');
                          if (ungrouped.length === 0) return null;
                          return (
                            <div className="space-y-1">
                              {hasGroups && <div className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Ungrouped</div>}
                              {ungrouped.map(account => {
                                const valid = account.hasValidSession === true;
                                const inUse = account.inUse === true;
                                const disabled = !valid || inUse;
                                return (
                                  <label key={account._id} className="flex items-center justify-between">
                                    <div className="flex items-center flex-1">
                                      <input
                                        type="checkbox"
                                        checked={formData.accountIds.includes(account._id)}
                                        onChange={e => {
                                          const ids = e.target.checked
                                            ? [...formData.accountIds, account._id]
                                            : formData.accountIds.filter(id => id !== account._id);
                                          setFormData({ ...formData, accountIds: ids });
                                        }}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        disabled={disabled}
                                      />
                                      <span className={`ml-2 text-sm ${disabled ? 'text-gray-400 dark:text-gray-500' : isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {account.displayName || account.username}
                                      </span>
                                    </div>
                                    {!valid && <span className="ml-2 text-xs text-red-600 dark:text-red-400">No Session</span>}
                                    {valid && inUse && <span className="ml-2 text-xs text-orange-600 dark:text-orange-400">In Use</span>}
                                    {valid && !inUse && <span className="ml-2 text-xs text-green-600 dark:text-green-400">Ready</span>}
                                  </label>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </div>

                {/* Source accounts */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Source Accounts (For You discovery) ({formData.sourceAccountIds.length} selected)
                  </label>
                  <div className={`max-h-32 overflow-y-auto rounded-lg border p-2 space-y-1 ${isDarkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-300 bg-gray-50'}`}>
                    {sourceAccounts.length === 0 && <p className="text-xs text-gray-400">No source accounts (add 'source' tag)</p>}
                    {sourceAccounts.map(acc => (
                      <label key={acc._id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.sourceAccountIds.includes(acc._id)}
                          onChange={() => {
                            const ids = formData.sourceAccountIds.includes(acc._id)
                              ? formData.sourceAccountIds.filter(id => id !== acc._id)
                              : [...formData.sourceAccountIds, acc._id];
                            setFormData({ ...formData, sourceAccountIds: ids });
                          }}
                          className="rounded"
                        />
                        <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>@{acc.username}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* View checker accounts */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    View Checker Accounts ({formData.viewCheckerAccountIds.length} selected)
                    <span className={`text-xs font-normal ml-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      — accounts tagged 'view_checker' used to fetch view counts
                    </span>
                  </label>
                  <div className={`max-h-32 overflow-y-auto rounded-lg border p-2 space-y-1 ${isDarkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-300 bg-gray-50'}`}>
                    {viewCheckerAccounts.length === 0 && <p className="text-xs text-gray-400">No view checker accounts (add 'view_checker' tag to accounts)</p>}
                    {viewCheckerAccounts.map(acc => (
                      <label key={acc._id} className="flex items-center justify-between text-sm cursor-pointer">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formData.viewCheckerAccountIds.includes(acc._id)}
                            onChange={() => {
                              const ids = formData.viewCheckerAccountIds.includes(acc._id)
                                ? formData.viewCheckerAccountIds.filter(id => id !== acc._id)
                                : [...formData.viewCheckerAccountIds, acc._id];
                              setFormData({ ...formData, viewCheckerAccountIds: ids });
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            disabled={!acc.hasValidSession}
                          />
                          <span className={`${!acc.hasValidSession ? 'text-gray-400 dark:text-gray-500' : isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            @{acc.username}
                            {acc.group ? ` (${acc.group})` : ''}
                          </span>
                        </div>
                        {!acc.hasValidSession && <span className="text-xs text-red-500">No Session</span>}
                        {acc.hasValidSession && <span className="text-xs text-green-500">Ready</span>}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Settings grid */}
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Max Tweet Age (min)', key: 'maxTweetAgeMinutes', min: 1, max: 1440 },
                    { label: 'Max Comments/Hour/Account', key: 'maxCommentsPerHourPerAccount', min: 1, max: 60 },
                    { label: 'Max Comments/Day/Account', key: 'maxCommentsPerDayPerAccount', min: 1, max: 500 },
                    { label: 'Delay Min (sec)', key: 'delayBetweenCommentsMin', min: 5 },
                    { label: 'Delay Max (sec)', key: 'delayBetweenCommentsMax', min: 10 },
                    { label: 'Min Author Followers', key: 'minAuthorFollowers', min: 0 },
                    { label: 'Max Author Followers (0=no limit)', key: 'maxAuthorFollowers', min: 0 }
                  ].map(field => (
                    <div key={field.key}>
                      <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{field.label}</label>
                      <input
                        type="number"
                        value={formData[field.key]}
                        min={field.min}
                        max={field.max}
                        onChange={e => setFormData({ ...formData, [field.key]: parseInt(e.target.value) || 0 })}
                        className={`w-full rounded-lg border px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className={`px-4 py-2 text-sm font-medium rounded-lg ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                >
                  {editingConfig ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoComments;
